import _ from 'underscore';
import { useCallback, useContext, useMemo } from 'react';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  usePreference,
  preferenceNames,
  useGetDefaultBudgetValueForCountry,
  useGetSupportedCountryList,
  useLoadAndUpdateUserBusinessInfo,
} from '@bingads-webui-campaign-react/pmax-lite-data';
import {
  globalizationModelSetup,
  LANGUAGE_CODE_MAPPING,
  COUNTRY_DEFAULT_LANGUAGE,
} from '@bingads-webui-campaign/ads-globalization';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import { useWizardService, getWizardData, WIZARD_EVENTS } from '@adsui/wizard';
import { readCampaign, createOrUpdateCampaign } from '@bingads-webui-campaign-react/perf-max-data';
import { PMAX_LITE_WIZARD_STEP_PATHS } from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';

import { isSignupRunningProxy, parsePhone, composePhone } from './business-info-helper';

function isKeyIncludedInArray(arr = [], key) {
  if (_.isEmpty(arr) || !key) {
    return false;
  }

  return arr.includes(key.toLowerCase()) || arr.includes(key.toUpperCase());
}

export const useLanguageCountryRepository = () => {
  const { scope } = useContext(ScopeContext);
  const permissions = useContext(PermissionsContext);
  const serverContext = useContext(ServerContext);
  const { CurrentLocale, UserRegion, CurrentUser } = serverContext || {};
  const { Market, Language } = CurrentUser || {};
  const isDebugging = isSignupRunningProxy();

  const { state, send } = useWizardService();
  const { cache } = getWizardData(state);

  const isCollectBusinessInfoEnabled = usePilotsValue('IsPmaxLiteCollectBusinessInfoEnabled');

  const { getDefaultBudgetValueForCountry } = useGetDefaultBudgetValueForCountry();
  const { getSupportedCountryList } = useGetSupportedCountryList();
  const { loadUserContactInfo, updateUserContactInfo } = useLoadAndUpdateUserBusinessInfo();

  const { LANGUAGES } = globalizationModelSetup({ permissions, culture: CurrentLocale });

  const instrumentation = useContext(InstrumentationContext);
  const activity = useMemo(() => {
    if (instrumentation && instrumentation.pmaxLiteSignup) {
      const scenario = instrumentation.pmaxLiteSignup.create();
      return scenario.languageCountryStep.create();
    }

    return { trace: _.noop, error: _.noop };
  }, [instrumentation]);

  const [{ value: languagePreference }, { update: updateLanguagePreference }]
    = usePreference(preferenceNames.SelectedLanguage);
  const [{ value: countryPreference }, { update: updateCountryPreference }]
    = usePreference(preferenceNames.SelectedCountry);
  const [{ value: mktPrefillEnabledPreference }, { update: updateMktPrefillEnabledPreference }]
    = usePreference(preferenceNames.MarketPrefillEnabled);
  const [{ value: languageCountryStepTAndC }, { update: updateLanguageCountryStepTAndCPreference }]
    = usePreference(preferenceNames.LanguageCountryPageTermsAndConditions);
  const [{ value: currencyPreference }] = usePreference(preferenceNames.SelectedCurrency);

  const gotoAdAssetStepDirectly = cache && mktPrefillEnabledPreference;

  const load = useCallback(() => {
    const { cid: currentCid, aid: currentAid, campaignId: currentCampaignId } = scope.values;
    let languagePromise = language => Promise.resolve({ selectedLanguage: language });
    if (currentCampaignId) {
      languagePromise = language => readCampaign(currentCid, currentAid, currentCampaignId).then((campaign) => {
        const { Languages } = campaign;
        let selectedLanguage = language;
        if (!_.isEmpty(Languages)) {
          selectedLanguage = _.first(Languages);
        }
        return {
          selectedLanguage,
        };
      }).catch((error) => {
        // TODO: handle error
        throw error;
      });
    }
    const countryPromise = country => Promise.resolve({ selectedCountry: country });
    const getSupportedCountryListPromise = () => getSupportedCountryList();
    const loadUserContactInfoPromise = isCollectBusinessInfoEnabled
      ? () => loadUserContactInfo()
      : () => Promise.resolve({});

    // once loaded, we should resume load isolated step again.
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { loadWizardDirectly: false });

    return getSupportedCountryListPromise()
      .then((countryListData) => {
        const allCountryCode = _.map(countryListData, c => c.Code);
        let currentCountry = countryPreference;
        if (mktPrefillEnabledPreference) {
          const userCountry = UserRegion || Market;
          if (isKeyIncludedInArray(allCountryCode, userCountry)) {
            currentCountry = userCountry.toUpperCase();
          }
        }

        let currentLanguage = languagePreference;
        if (mktPrefillEnabledPreference) {
          const countryDefaultLanguage = COUNTRY_DEFAULT_LANGUAGE[currentCountry];
          if (LANGUAGES.includes(countryDefaultLanguage)) {
            currentLanguage = countryDefaultLanguage;
          } else {
            const browserLanguageCode = _.get(navigator, ['language'], 'en').substring(0, 2);
            const userLanguageCode = (Language || browserLanguageCode).toLowerCase();
            if (userLanguageCode && LANGUAGE_CODE_MAPPING[userLanguageCode]
              && LANGUAGES.includes(LANGUAGE_CODE_MAPPING[userLanguageCode])) {
              currentLanguage = LANGUAGE_CODE_MAPPING[userLanguageCode];
            }
          }
        }

        return Promise.all([
          languagePromise(currentLanguage),
          countryPromise(currentCountry),
          loadUserContactInfoPromise(),
        ]).then(([languageData, countryData, userContactInfo = {}]) => ({
          data: {
            userContactInfo: {
              ...userContactInfo,
              ...(
                parsePhone(
                  userContactInfo.phone,
                  countryListData,
                  userContactInfo.phoneNumberCountryCode || currentCountry
                )
              ),
            },
            hideTerms: languageCountryStepTAndC,
            supportedCountryList: countryListData,
            ...languageData,
            ...countryData,
          },
        })).catch((error) => {
          activity.error(`[PMaxLiteSignup][LanguageCountryStep] Failed to load language and country. Error: ${JSON.stringify(error)}`);
          throw error;
        });
      });
  }, [LANGUAGES, Language, Market, UserRegion, activity,
    countryPreference, getSupportedCountryList, isCollectBusinessInfoEnabled,
    languageCountryStepTAndC, languagePreference, loadUserContactInfo,
    mktPrefillEnabledPreference, send, scope]);

  const save = useCallback(({ data }) => {
    const { campaignId: currentCampaignId } = scope.values;
    const {
      selectedLanguage, selectedCountry, userContactInfo = {}, terms = {}, hideTerms,
    } = data;
    updateLanguagePreference(selectedLanguage);
    updateCountryPreference(selectedCountry);
    // disable prefill when user save country manually.
    updateMktPrefillEnabledPreference(false);

    // if user not accept terms, or user contact info is not valid,
    // or user contact info is empty, then we should not save language and country.
    if (isCollectBusinessInfoEnabled) {
      const {
        phoneNumber,
        countryCode: phoneCountryCode,
      } = composePhone(userContactInfo.phoneNumber, userContactInfo.callingCountryCode, userContactInfo.callingCode);
      userContactInfo.phone = phoneNumber;
      userContactInfo.phoneNumberCountryCode = phoneCountryCode;
      if (
        (!hideTerms && !isDebugging && !terms.accepted)
        || !userContactInfo.isValid
        || (_.isEmpty(userContactInfo.phone) || _.isEmpty(userContactInfo.email))
      ) {
        return Promise.reject();
      }

      activity.trace(`[PMaxLiteSignup][LanguageCountryStep] phone and email submitted. phone: ${userContactInfo.phoneNumberCountryCode} : ${userContactInfo.phone}, email: ${userContactInfo.email}`);
      updateLanguageCountryStepTAndCPreference(true);
    }

    const updateUserPromise = isCollectBusinessInfoEnabled
      ? () => updateUserContactInfo(userContactInfo, terms.communication, !hideTerms)
      : () => Promise.resolve();

    activity.trace(`[PMaxLiteSignup][LanguageCountryStep] Language and country selected. Language: ${selectedLanguage}, Country: ${selectedCountry}`);

    // if user selection country is not US, then we should update the wizard flow to no smart page flow,
    // to avoid the scenario: user select smart page in next step and go back to change country to non-US.
    if (selectedCountry !== 'US') {
      send(WIZARD_EVENTS.UPDATE_FlOW_FACTORS, {
        flowFactors: {
          isSmartPage: false,
        },
      });
    }

    let languagePromise = () => Promise.resolve({ selectedLanguage });
    if (currentCampaignId) {
      // when currencyPreference is set, it means user already set budget value,
      // we should not update the budget value.
      languagePromise = () => getDefaultBudgetValueForCountry(selectedCountry)
        .then(budgetValue => createOrUpdateCampaign({
          Id: currentCampaignId,
          Languages: [selectedLanguage],
          ...(currencyPreference ? {} : {
            Budget: {
              Amount: budgetValue,
              Type: 'DailyStandard',
            },
          }),
        }).then(campaign => ({
          selectedLanguage: _.first(campaign.Languages),
        })).catch((error) => {
          activity.error(`[PMaxLiteSignup][LanguageCountryStep] Failed to update campaign with language. Error: ${JSON.stringify(error)}`);
          throw error;
        }));
    }
    const countryPromise = () => Promise.resolve({ selectedCountry });

    return Promise.all([
      languagePromise(),
      countryPromise(),
      updateUserPromise(),
    ]).then(([languageData, countryData]) => {
      // For cache flow, will jump to asset step directly for the first time.
      if (gotoAdAssetStepDirectly) {
        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isShowingAnimation: true });
        send(WIZARD_EVENTS.NAVIGATE_TO, {
          stepId: PMAX_LITE_WIZARD_STEP_PATHS.PREVIEW_ASSETS,
        });
      }

      return ({
        data: {
          ...languageData,
          ...countryData,
        },
      });
    }).catch((error) => {
      activity.error(`[PMaxLiteSignup][LanguageCountryStep] Failed to save language and country. Error: ${JSON.stringify(error)}`);
      throw error;
    });
  }, [activity, currencyPreference, getDefaultBudgetValueForCountry,
    gotoAdAssetStepDirectly, isCollectBusinessInfoEnabled, isDebugging,
    send, updateCountryPreference, updateLanguageCountryStepTAndCPreference,
    updateLanguagePreference, updateMktPrefillEnabledPreference, updateUserContactInfo, scope]);

  return {
    load,
    save,
  };
};
